import { pubSub } from '@utils/PubSub';

interface DYRecommendationContext {
  type: 'CART' | 'PRODUCT' | 'CATEGORY' | 'HOMEPAGE' | 'OTHER';
  data?: string[];
}

declare global {
  interface Window {
    DY?: { recommendationContext?: DYRecommendationContext };
  }
}

export default class Cart {
  private dyRecommendationContextOriginal: DYRecommendationContext | undefined =
    undefined;
  private cartFlyoutActive = false;

  constructor() {
    window.addEventListener('init-overlay', async (e: Event) => {
      if ((e as CustomEvent).detail !== 'cart') return;

      //Because the cart is a flyout, we must store the original value of DY's recommendation context so we can revert back to the original page context if the cart is closed.
      //Only capture the original context on the first open — subsequent init-overlay events are cart refreshes where the context is already CART.
      if (!this.cartFlyoutActive) {
        this.dyRecommendationContextOriginal = window.DY?.recommendationContext;
      }
      this.cartFlyoutActive = true;
      await this.setDYContext();
      pubSub.publish('theme:cart:upsell:fetch');
    });

    window.addEventListener('close-overlay', (e: Event) => {
      if ((e as CustomEvent).detail !== 'cart') return;
      this.cartFlyoutActive = false;
      if (window.DY) {
        //revert from cart recommendation context to original page's recommendation context
        window.DY.recommendationContext = this.dyRecommendationContextOriginal;
        this.updateDYContextScript(this.dyRecommendationContextOriginal);
      }
    });

    document.addEventListener('theme:add-to-cart', async (e) => {
      const item = e.detail;
      const hasGiftKey = item?.properties
        ? Object.keys(item.properties).includes('_isGiftCard')
        : false;
      const isGiftCard = hasGiftKey
        ? JSON.parse(item?.properties?._isGiftCard)
        : false;
      const itemInCart = await this.itemPresentInCart(item);
      const urgencySet =
        JSON.parse(sessionStorage.getItem('urgencySet')) ?? false;
      const urgencyEnabled =
        JSON.parse(sessionStorage.getItem('urgencyEnabled')) ?? false;
      const notRemoving = item.quantity > 0;

      if (!urgencySet && urgencyEnabled && notRemoving) {
        this.emitUrgencyEvent();
      }

      if (itemInCart && !isGiftCard) {
        this.updateCart(item);
      } else {
        this.addToCart(item);
      }
    });

    //YOTPO REWARDS CART TRIGGER
    if (
      window.location.href.includes('/rewards') ||
      window.location.href.includes('/account')
    ) {
      document.addEventListener('click', (e) => {
        const yotpoButtonText = e.target.parentNode
          .getAttribute('aria-label')
          ?.toLowerCase();

        if (yotpoButtonText == 'redeem') {
          //yotpo does not have any specific class identifiers for the different states of redemption buttons, nor their text containers. As a result, we must rely on aria label attributes and inner text to specifically target elements in the widget
          //when a user redeems a rewards product via Yotpo's 'redeem' button, fire a mutation observer that checks when a user has successfully added a reward product to their cart
          //when a product has been successfully added to the cart, update the flyout and trigger the opening

          // Select the node that will be observed for mutations
          const targetNodes = document.querySelectorAll(
            '.yotpo-redemption-option'
          );

          // Options for the observer (which mutations to observe)
          const config = { childList: true, subtree: true };

          // Callback function to execute when mutations are observed
          const callback = (mutationList, observer) => {
            for (const mutation of mutationList) {
              if (
                mutation.addedNodes?.[0]?.innerText?.includes(
                  'Product has been successfully added to your cart'
                )
              ) {
                this.getCartResponse().then((data) => {
                  this.updateCartIconQuantities(data);
                  this.emitFetch();
                  observer.disconnect();
                });
              }
            }
          };

          const observer = new MutationObserver(callback);

          // Start observing the target node for configured mutations
          targetNodes.forEach((node) => {
            observer.observe(node, config);
          });
        }
      });
    }

    document.addEventListener('theme:clear-cart', () => {
      this.clearCart();
    });

    this.sienaCartUpdate();
  }

  /**
   * Updates window.DY.recommendationContext with the current cart's variant IDs.
   * Only runs while the cart flyout is active.
   */
  async setDYContext() {
    if (!window.DY || !this.cartFlyoutActive) return;

    const { items } = await this.getCartResponse();

    //Pass an array of variantIds to window.DY.recommendationContext. Do not pass free gift item variant IDs, and in the case of checkout bundle items, pass the original bundle variant ID.
    const variantIds =
      items
        ?.filter(
          (item) =>
            !item.properties?._freeGift &&
            !item.properties?._yotpo_loyalty_discount_type
        )
        .map((item) => {
          const parentInstance =
            item.properties?._product_bundle_parent_instance;
          if (!parentInstance) return String(item.id);
          return parentInstance.split('_').at(-1) ?? '';
        }) ?? [];

    window.DY.recommendationContext = { type: 'CART', data: variantIds };
    this.updateDYContextScript(window.DY.recommendationContext);
    window.dispatchEvent(new CustomEvent('dy:context:set'));
  }

  //In addition to the window object, we must also update the DOM so DY's native mutation observers run properly when dynamically updating page context
  updateDYContextScript(context: DYRecommendationContext | undefined) {
    const script = document.querySelector('#dy-recommendation-context');
    if (!script || !context) return;
    script.textContent = `window.DY = window.DY || {}; DY.recommendationContext = ${JSON.stringify(
      context
    )};`;
  }

  async getCartResponse(): Promise<{
    items?: Array<{
      id: number;
      key: string;
      quantity: number;
      properties?: Record<string, string>;
    }>;
  }> {
    const cart = await fetch('/cart.json');
    return cart.json();
  }

  sienaCartUpdate() {
    document.body.addEventListener('siena:cart:updated', (e: Event) => {
      const eventDetails = (e as CustomEvent).detail;
      this.updateCartIconQuantities(eventDetails.cart);
      this.emitFetch();
      this.setDYContext();

      try {
        window.heap.track('Theme - Siena - Add to Cart');
      } catch (e) {
        throw new Error(`Heap error: ${e}`);
      }
    });
  }

  async itemPresentInCart(payload) {
    const { items } = await this.getCartResponse();
    const matches = this.findCartItem(payload, items);
    return matches.length > 0;
  }

  findCartItem(payload, cartItems) {
    return cartItems.filter((item) => {
      const hasMatchingId = item.id.toString() === payload.id;
      const hasMatchingKey = item.key === payload.id;
      return hasMatchingId || hasMatchingKey;
    });
  }

  emitFetch() {
    return pubSub.publish('cart:request');
  }

  emitUrgencyEvent() {
    const now = new Date();
    sessionStorage.setItem('urgencySet', true);
    sessionStorage.setItem('urgencyStart', now);
    pubSub.publish('cart:urgency:start');
  }

  addToCart(formData) {
    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formData),
    })
      .then((response) => {
        return response.json();
      })
      .then(() => {
        fetch('/cart.js')
          .then((response) => {
            return response.json();
          })
          .then((cart) => {
            this.updateCartIconQuantities(cart);
            this.emitFetch();
            this.setDYContext();
          });
      })
      .catch((error) => {
        throw new Error(`Add to cart form data error, ${error}`);
      });
  }

  async updateCart(payload) {
    const request = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    };

    const response = await fetch('/cart/change.js', request).then((res) =>
      res.json()
    );

    this.updateCartIconQuantities(response);
    this.emitFetch();
    this.setDYContext();
  }

  clearCart() {
    fetch(window.Shopify.routes.root + 'cart/clear.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    })
      .then((response) => {
        this.updateCartIconQuantities();
        this.setDYContext();
        return response.json();
      })
      .catch((error) => {
        throw new Error(`Clear cart error, ${error}`);
      });
  }

  updateCartIconQuantities(cart?: { items? }) {
    let itemCount = 0;
    if (cart?.items) {
      cart.items.forEach((item) => {
        const included =
          !item?.properties?._freeGift &&
          !item?.properties?._yotpo_loyalty_discount_type;
        if (included) itemCount = itemCount + item.quantity;
      });
    }

    document.querySelectorAll('.header__icon--cart').forEach((icon) => {
      itemCount > 0
        ? icon.querySelector('svg').classList.add('cart-populated')
        : icon.querySelector('svg').classList.remove('cart-populated');
      icon
        .querySelector('.cart-count-bubble')
        .setAttribute('data-cart-size', `${itemCount}`);
      icon.querySelector('span[aria-hidden="true"]').innerHTML = `${itemCount}`;
    });
  }
}
